import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { Chat } from '../../../../../../bayadic-shared-models/chat/Chat';
import { QuizMessage } from '../../../../../../bayadic-shared-models/classes/quiz/quiz-message';
import { TranslateModule } from '@ngx-translate/core';
import { IonicModule } from '@ionic/angular';
import { QuizDataService } from '../../../services/quiz-data.service';
import { NavigationService } from '../../../../../services/navigation.service';
import { UserManagerProvider } from '../../../../../../providers/user-manager/user-manager';
import { ChatsDatabaseService } from '../../../../../../providers/chats-database-service/chats-database-service';
import { SocketManagerProvider } from '../../../../../../providers/socket-manager/socket-manager';
import { ACTIONS } from '../../../../../../bayadic-shared-models/enums/Enums';
import { UpdateMessageBulkRequest } from '../../../../../../bayadic-shared-models/item/update-message-bulk-request';
import { UpdateMessageBulkResponse } from '../../../../../../bayadic-shared-models/item/update-message-bulk-response';
import { CompHeaderComponent } from "../../../../../../components/comp-header/comp-header";

@Component({
  standalone: true,
  selector: 'quiz-results',
  templateUrl: 'quiz-results.html',
  styleUrls: ['quiz-results.scss'],
  imports: [CommonModule, TranslateModule, IonicModule, CompHeaderComponent]
})
export class QuizResultsComponent implements OnInit {
  chat: Chat;
  quizMessages: QuizMessage[];
  totalQuestions: number = 0;
  correctAnswers: number = 0;
  wrongAnswers: number = 0;
  totalScore: number = 0;
  hasPassed: boolean = false;

  constructor(
    private navService: NavigationService,
    private route: ActivatedRoute,
    private quizDataService: QuizDataService,
    private userManager: UserManagerProvider,
    private chatsDatabaseService: ChatsDatabaseService,
    private socketManager: SocketManagerProvider
  ) {}

  ngOnInit() {
    const quizData = this.quizDataService.getQuizData();
    if (!quizData) {
      // If no quiz data is available, navigate back to chat
      const chatId = this.route.snapshot.paramMap.get('chatId');
      this.navService.navigateTo('/chat/'+chatId);
      return;
    }

    this.chat = quizData.chat;
    this.quizMessages = quizData.quizMessages;
    this.calculateResults();
  }

  private calculatePointsForQuestion(quizMessage: QuizMessage): number {
    let points = 0;
    const submission = quizMessage.content['submission'];
    if (!submission) return 0;

    // Get all correct answer indices
    const correctAnswerIndices = quizMessage.content.lesson.questions[0].choices
      .map((choice, index) => ({ index, isCorrect: choice.isCorrect }))
      .filter(item => item.isCorrect)
      .map(item => item.index);
    
    // Check each selected answer
    submission.selectedIndices?.forEach(index => {
      const selectedChoice = quizMessage.content.lesson.questions[0].choices[index];
      if (selectedChoice.isCorrect) {
        points += 40;
      } else {
        points -= 10;
      }
    });
    
    // Check for unselected correct answers
    correctAnswerIndices.forEach(index => {
      if (!submission.selectedIndices?.includes(index)) {
        points -= 10; // Penalty for missing a correct answer
      }
    });

    return points;
  }

  private async updateQuizMessagesWithPoints() {
    for (const quizMessage of this.quizMessages) {
      if (quizMessage.content['submission']) {
        const points = this.calculatePointsForQuestion(quizMessage);
        quizMessage.content['submission'].points = points;
        
        // Update the message in the database
        await this.chatsDatabaseService.updateMessageByChat(this.chat, quizMessage);
      }
    }

    // Send bulk update to server
    try {
      await this.socketManager.asyncQuery<UpdateMessageBulkResponse, UpdateMessageBulkRequest>(ACTIONS.UPDATE_MESSAGE_BULK, {
        messages: this.quizMessages.map(message => ({
          messageID: message.messageID,
          message: {
            content: message.content,
            type: message.messageType
          }
        }))
      });
    } catch (error) {
      console.error('Failed to update quiz points:', error);
    }
  }

  private getCorrectAnswerIndices(question: any): number[] {
    return question.choices
      .map((choice: any, index: number) => ({ index, isCorrect: choice.isCorrect }))
      .filter((item: any) => item.isCorrect)
      .map((item: any) => item.index);
  }

  private validateAnswer(submission: any, question: any): boolean {
    const correctAnswerIndices = this.getCorrectAnswerIndices(question);
    
    // Check if all correct answers are selected
    const hasAllCorrect = correctAnswerIndices.every(index => 
      submission.selectedIndices.includes(index)
    );
    
    return hasAllCorrect;
  }

  private updateUserPoints() {
    this.userManager.user.points += this.totalScore;
    return this.userManager.save();
  }

  private processQuizMessage(quizMessage: QuizMessage) {
    if (!quizMessage.content['submission']) return;

    const submission = quizMessage.content['submission'];
    const question = quizMessage.content.lesson.questions[0];
    
    const isQuestionCorrect = this.validateAnswer(submission, question);

    if (isQuestionCorrect) {
      this.correctAnswers++;
    } else {
      this.wrongAnswers++;
    }
    this.totalScore += submission.points;
    this.totalQuestions++;
  }

  private resetCounters() {
    this.totalQuestions = 0;
    this.correctAnswers = 0;
    this.wrongAnswers = 0;
    this.totalScore = 0;
  }

  async calculateResults() {
    // Reset counters
    this.resetCounters();

    // Update quiz messages with calculated points
    await this.updateQuizMessagesWithPoints();

    // Process each quiz message in the last section
    this.quizDataService.getQuizData().quizMessages.forEach(quizMessage => this.processQuizMessage(quizMessage));

    // Calculate pass/fail based on average score per question
    const averageScore = this.totalQuestions > 0 ? this.totalScore / this.totalQuestions : 0;
    this.hasPassed = averageScore >= 20; // Pass if average score is 20 or more per question

    // Update user points
    await this.updateUserPoints();
  }

  goBack() {
    this.quizDataService.clearQuizData();
    this.navService.navigateTo('/chat/'+this.chat.chatID);
  }
} 